<template>
  <div v-if="!loadingState" class="d-flex flex-column h-100">
    <navigation-top/>

    <div v-if="starredWorkflows?.length > 0" style="margin-top: 38px; margin-bottom: 24px;">
      <div class="d-flex justify-space-between" style="margin-bottom: 18px;">
        <span>Starred Workflows</span>
        <sort-dropdown
          :items="workflowSortingOptions"
          :default-value="starredSortBy"
          @onSelect="starredSortBy = $event"
        />
      </div>
      <div class="dashboard-grid-layout" ref="starredGrid">
        <div
          v-for="workflow in starredWorkflows"
          :key="workflow.id"
          :data-workflow-id="workflow.id"
          class="starred-drag-item"
        >
          <v-btn
            v-if="starredSortBy === 'default'"
            class="drag-handle"
            icon="mdi-drag-variant"
            variant="text"
            size="small"
            @click.prevent
          />
          <dashboard-workflow-card
            :workflow="{ ...workflow, projectName: project?.name, organisationName: project?.organisation?.name }"
            @edit="editWorkflow"
          ></dashboard-workflow-card>
        </div>
      </div>
    </div>

    <div style="margin-top: 38px; margin-bottom: 24px;">
      <div class="d-flex justify-space-between" style="margin-bottom: 18px;">
        <span>{{ starredWorkflows?.length > 0 ? 'Other workflows' : 'All Workflows' }}</span>
        <sort-dropdown
          :items="workflowSortingOptions"
          :default-value="workflowsSortBy"
          @onSelect="workflowsSortBy = $event"
        />
      </div>
      <div class="dashboard-grid-layout" ref="workflowGrid">
        <workflow-dialog ref="dialogWorkflow" :setWorkflow="true" v-if="subscriptionAccessType === 'FULL'">
          <template #activator="{ props }">
            <div v-bind="props" class="dashboard-card text-new-button d-flex flex-column justify-center align-center">
              <v-icon style="font-size: 40px;" class="mb-2">mds:add</v-icon>
              New Workflow
            </div>
          </template>
        </workflow-dialog>

        <div
          v-for="workflow in notStarredWorkflows"
          :key="workflow.id"
          :data-workflow-id="workflow.id"
          class="workflow-drag-item"
        >
          <v-btn
            v-if="workflowsSortBy === 'default'"
            class="drag-handle"
            icon="mdi-drag-variant"
            variant="text"
            size="small"
            @click.prevent
          />
          <dashboard-workflow-card
            :hide-actions="subscriptionAccessType !== 'FULL'"
            :workflow="{ ...workflow, projectName: project?.name, organisationName: project?.organisation?.name }"
            @edit="editWorkflow"
          ></dashboard-workflow-card>
        </div>
      </div>
    </div>

    <div class="dashboard-container-alt d-flex flex-column pb-10" style="padding-top: 48px; margin-top: 40px;">
      <div class="d-flex">
        <v-icon class="pt-4 mr-2">mds:account-circle</v-icon>
        <div class="pt-3">Project members</div>

        <v-spacer/>
        <invite-team-member-dialog>
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="large"
            >
              <v-icon class="mr-2">mds:group_add</v-icon>
              Invite members
            </v-btn>
          </template>
        </invite-team-member-dialog>
      </div>

      <v-data-table
        style="margin-top: 24px;"
        :headers="projectMemberHeaders"
        :items="[...activeMembers, ...pendingMembers]"
        :items-per-page="-1"
      >
        <template v-slot:bottom></template>

        <template #item="props">
          <tr :class="{'highlight-owner' : isProjectOwner(props.item, project)}">
            <template v-if="props.item.user">
              <td>{{ props.item.user.email }}</td>
              <td>
                {{ props.item.user.firstname }} {{ props.item.user.lastname }}
                <v-tooltip v-if="isProjectOwner(props.item, project)" location="bottom">
                  <template v-slot:activator="{ props: innerProps }">
                    <span v-bind="innerProps"> (Project Owner)</span>
                  </template>
                  <span>The owner of this project.</span>
                </v-tooltip>
                <v-tooltip v-if="isLoggedInUser(props.item)" location="bottom">
                  <template v-slot:activator="{ props: innerProps }">
                    <span class="font-weight-bold" v-bind="innerProps"> (You)</span>
                  </template>
                  <span>This logged in user.</span>
                </v-tooltip>
              </td>
            </template>
            <template v-else>
              <td>{{ props.item.email }}</td>
              <td><i>Invitation pending</i></td>
            </template>
            <td>{{ props.item.user?.company || '-' }}</td>
            <td>{{ props.item.user?.job || '-' }}</td>
            <td>
              <v-menu>
                <template #activator="{ props }">
                  <v-btn
                    variant="text"
                    icon="mds:more_horiz"
                    v-bind="props"
                  />
                </template>
                <v-list>
                  <v-tooltip
                    location="bottom"
                    :disabled="canRemoveTeamMember(props.item)"
                  >
                    <template v-slot:activator="{ props: tooltipProps }">
                      <div v-bind="tooltipProps">
                        <v-list-item
                          :disabled="!canRemoveTeamMember(props.item)"
                          @click="showDeleteTeamMemberDialog($router, props.item, project.id)"
                        >
                          <v-list-item-title>
                            {{ isLoggedInUser(props.item) ? 'Leave project' : 'Remove from project' }}
                            <span v-if="isProjectOwner(props.item, project) && !isLoggedInUser(props.item)">(cannot remove owner)</span>
                          </v-list-item-title>
                        </v-list-item>
                      </div>
                    </template>

                    <span>{{ removeTeamMemberTooltip(props.item) }}</span>
                  </v-tooltip>

                  <v-tooltip
                    location="bottom"
                    :disabled="ownsProject && !isProjectOwner(props.item, project) && !!props.item.userId"
                  >
                    <template v-slot:activator="{ props: tooltipProps }">
                      <div v-bind="tooltipProps">
                        <v-list-item
                          :disabled="!ownsProject || isProjectOwner(props.item, project) || !props.item.userId"
                          @click="showUpdateProjectOwnerDialog(props.item, project.id)"
                        >
                          <v-list-item-title>Assign Project Owner</v-list-item-title>
                        </v-list-item>
                      </div>
                    </template>

                    <span v-if="!ownsProject">Only the current project owner can assign a new project owner.</span>
                    <span v-else-if="!props.item.userId">This person has not registered yet.</span>
                    <span v-else>This user is already the project owner.</span>
                  </v-tooltip>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
    <delete-confirmation-dialog
      v-model="deleteDialog.show"
      :title="deleteDialog.title"
      :text="deleteDialog.text"
      :onDelete="deleteDialog.action"
    />
    <confirmation-dialog
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      :text="confirmDialog.text"
      :confirm-label="confirmDialog.confirmLabel"
      :cancel-label="confirmDialog.cancelLabel"
      @confirm="confirmDialog.action"
    />
  </div>
</template>

<script>
import Sortable from 'sortablejs'
import { mapState, mapGetters } from 'vuex'
import store from '@/store'
import SortDropdown from '@/components/SortDropdown'
import NavigationTop from '@/components/NavigationTop'
import WorkflowDialog from '@/components/WorkflowDialog'
import ConfirmationDialog from '@/components/ConfirmationDialog'
import DashboardWorkflowCard from '@/components/DashboardWorkflowCard'
import InviteTeamMemberDialog from '@/components/InviteTeamMemberDialog'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog'
import projectApi from '@/api/projectApi'
import { readStorage, writeStorage } from '@/utils/storage'

const WORKFLOWS_SORT_STORAGE_KEY = 'qlerify.project.workflowsSortBy'
const STARRED_SORT_STORAGE_KEY = 'qlerify.project.starredSortBy'

export default {
  components: {
    WorkflowDialog,
    InviteTeamMemberDialog,
    DashboardWorkflowCard,
    NavigationTop,
    DeleteConfirmationDialog,
    ConfirmationDialog,
    SortDropdown
  },
  props: {},
  data () {
    return {
      workflowsSortBy: readStorage(WORKFLOWS_SORT_STORAGE_KEY, 'default'),
      starredSortBy: readStorage(STARRED_SORT_STORAGE_KEY, 'default'),
      workflowSortingOptions: [
        { title: 'Default', value: 'default' },
        { title: 'Last modified', value: 'last_modified' }
      ],
      customWorkflowOrder: [],
      customStarredOrder: [],
      sortableInstance: null,
      starredSortableInstance: null,
      deleteDialog: {
        show: false,
        title: 'Confirm team member deletion',
        text: 'Are you sure you want to remove the team member?',
        action: null
      },
      confirmDialog: {
        show: false,
        title: '',
        text: '',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        action: null
      },
      projectMemberHeaders: [
        { title: 'E-mail', key: 'email' },
        { title: 'Name', key: 'name' },
        { title: 'Company', key: 'company' },
        { title: 'Team/ Job Title', key: 'jobTitle' },
        { title: 'Actions', sortable: false, key: 'actions' }
      ]
    }
  },
  computed: {
    ...mapState({
      workflows: state => state.workflows.workflows,
      listTeamMembers: state => state.teamMembers.teamMembers,
      projectMemberships: state => state.projectMemberships.projectMemberships,
      currentUser: state => state.user.user,
      userInfo: state => state.userInfo,
      subscriptionAccessType: state => state.payments.subscriptionAccessType
    }),
    ...mapGetters({
      project: 'projectMemberships/activeProject',
      loadingState: 'loading/getLoadingState'
    }),
    activeMembers () {
      if (!Array.isArray(this.listTeamMembers) || !this.listTeamMembers.length) {
        return []
      }

      return this.listTeamMembers.filter(({ lastLogin }) => lastLogin)
    },

    pendingMembers () {
      if (!Array.isArray(this.listTeamMembers) || !this.listTeamMembers.length) {
        return []
      }

      return this.listTeamMembers.filter(({ lastLogin }) => !lastLogin)
    },

    starredWorkflows () {
      const res = []
      for (const workflow of (this.workflows || [])) {
        if (workflow.starred) {
          res.push(workflow)
        }
      }
      if (this.starredSortBy === 'default' && this.customStarredOrder.length > 0) {
        return [...res].sort((a, b) => {
          const posA = this.customStarredOrder.indexOf(a.id)
          const posB = this.customStarredOrder.indexOf(b.id)
          return (posA === -1 ? Infinity : posA) - (posB === -1 ? Infinity : posB)
        })
      }
      return this.sortWorkflows(res)
    },

    notStarredWorkflows () {
      const res = []
      for (const workflow of (this.workflows || [])) {
        if (!workflow.starred) {
          res.push(workflow)
        }
      }
      if (this.workflowsSortBy === 'default' && this.customWorkflowOrder.length > 0) {
        return [...res].sort((a, b) => {
          const posA = this.customWorkflowOrder.indexOf(a.id)
          const posB = this.customWorkflowOrder.indexOf(b.id)
          return (posA === -1 ? Infinity : posA) - (posB === -1 ? Infinity : posB)
        })
      }
      return this.sortWorkflows(res)
    },

    ownsProject () {
      return this.currentUser?.id === this.project?.organisation?.userId
    }
  },

  watch: {
    workflowsSortBy (val) {
      writeStorage(WORKFLOWS_SORT_STORAGE_KEY, val)
      if (val === 'default') {
        this.loadCustomOrder()
        this.$nextTick(() => this.initSortable())
      } else {
        this.destroySortable()
      }
    },

    starredSortBy (val) {
      writeStorage(STARRED_SORT_STORAGE_KEY, val)
      if (val === 'default') {
        this.loadCustomOrder()
        this.$nextTick(() => this.initStarredSortable())
      } else {
        this.destroyStarredSortable()
      }
    },

    loadingState (val) {
      if (!val && this.workflowsSortBy === 'default' && !this.sortableInstance) {
        this.$nextTick(() => this.initSortable())
      }
      if (!val && this.starredSortBy === 'default' && !this.starredSortableInstance) {
        this.$nextTick(() => this.initStarredSortable())
      }
    },

    project (val) {
      if (val) {
        this.loadCustomOrder()
      }
    },

    listTeamMembers: {
      handler (newList) {
        if (!this.userInfo) {
          return
        }

        const isUserInList = newList?.some(member => member.user?.email === this.userInfo?.toLowerCase())

        if (!isUserInList) {
          store.dispatch('projectMemberships/listProjectMembershipsForCurrentUser')
          this.$router.replace('/404')
        }
      },
      deep: true
    }
  },

  methods: {
    isProjectOwner: (teamMember, project) => teamMember.userId === project?.organisation?.userId,
    isLoggedInUser (teamMember) {
      return teamMember.userId === this.currentUser?.id
    },
    canRemoveTeamMember (teamMember) {
      if (this.isProjectOwner(teamMember, this.project)) {
        return false
      }

      return this.ownsProject || this.isLoggedInUser(teamMember)
    },

    removeTeamMemberTooltip (teamMember) {
      if (this.isProjectOwner(teamMember, this.project)) {
        return this.isLoggedInUser(teamMember)
          ? 'Assign another team member as project owner before leaving the project.'
          : 'The project owner cannot be removed from the project.'
      }

      return 'Only the project owner can remove other members.'
    },

    showDeleteTeamMemberDialog (router, item, projectId) {
      const isSelf = this.isLoggedInUser(item)
      this.deleteDialog = {
        show: true,
        title: isSelf ? 'Confirm leaving the project' : 'Confirm team member deletion',
        text: isSelf
          ? 'You are about to remove yourself from this project and will not be able to access its workflows anymore.'
          : 'Are you sure you want to remove the team member?',
        action: () => this.deleteTeamMember(router, item, projectId)
      }
    },
    deleteTeamMember: projectApi.deleteTeamMember,

    showUpdateProjectOwnerDialog (item, projectId) {
      if (this.isLoggedInUser(item)) {
        return
      }
      this.confirmDialog = {
        show: true,
        title: 'Confirm project owner change',
        text: 'Are you sure you want to assign this user as the new project owner?',
        confirmLabel: 'Assign',
        cancelLabel: 'Cancel',
        action: () => {
          this.updateProjectOwner(item, projectId)
          this.confirmDialog.show = false
        }
      }
    },
    updateProjectOwner: projectApi.updateProjectOwner,

    editWorkflow (item) {
      this.$refs.dialogWorkflow.setItem(Object.assign({}, item))
      this.$refs.dialogWorkflow.openWorkflowDialog()
    },

    sortWorkflows (workflows) {
      return [...workflows].sort((a, b) => {
        return new Date(b.updatedAt) - new Date(a.updatedAt)
      })
    },

    loadCustomOrder () {
      const membership = this.projectMemberships?.find(m => m.project?.id === this.project?.id)
      this.customWorkflowOrder = membership?.workflowOrder || []
      this.customStarredOrder = membership?.starredWorkflowOrder || []
    },

    saveCustomOrder (order) {
      projectApi.updateWorkflowOrder(this.project?.id, order)
    },

    saveStarredOrder (order) {
      projectApi.updateStarredWorkflowOrder(this.project?.id, order)
    },

    initSortable () {
      this.destroySortable()
      const grid = this.$refs.workflowGrid
      if (!grid) return
      this.sortableInstance = new Sortable(grid, {
        draggable: '.workflow-drag-item',
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        onStart: () => { this.$refs.workflowGrid?.classList.add('is-dragging') },
        onEnd: this.onDragEnd
      })
    },

    destroySortable () {
      if (this.sortableInstance) {
        this.sortableInstance.destroy()
        this.sortableInstance = null
      }
    },

    initStarredSortable () {
      this.destroyStarredSortable()
      const grid = this.$refs.starredGrid
      if (!grid) return
      this.starredSortableInstance = new Sortable(grid, {
        draggable: '.starred-drag-item',
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        onStart: () => { this.$refs.starredGrid?.classList.add('is-dragging') },
        onEnd: this.onStarredDragEnd
      })
    },

    destroyStarredSortable () {
      if (this.starredSortableInstance) {
        this.starredSortableInstance.destroy()
        this.starredSortableInstance = null
      }
    },

    onDragEnd () {
      const grid = this.$refs.workflowGrid
      if (!grid) return
      const newOrder = Array.from(grid.querySelectorAll('.workflow-drag-item'))
        .map(el => el.getAttribute('data-workflow-id'))
      this.customWorkflowOrder = newOrder
      this.saveCustomOrder(newOrder)
      setTimeout(() => grid.classList.remove('is-dragging'), 200)
    },

    onStarredDragEnd () {
      const grid = this.$refs.starredGrid
      if (!grid) return
      const newOrder = Array.from(grid.querySelectorAll('.starred-drag-item'))
        .map(el => el.getAttribute('data-workflow-id'))
      this.customStarredOrder = newOrder
      this.saveStarredOrder(newOrder)
      setTimeout(() => grid.classList.remove('is-dragging'), 200)
    }

  },

  mounted () {
    this.loadCustomOrder()
    if (!this.loadingState) {
      if (this.workflowsSortBy === 'default') {
        this.$nextTick(() => this.initSortable())
      }
      if (this.starredSortBy === 'default') {
        this.$nextTick(() => this.initStarredSortable())
      }
    }
  },

  beforeUnmount () {
    this.destroySortable()
    this.destroyStarredSortable()
  }
}
</script>

<style scoped>
.highlight-owner > td {
  font-weight: bold;
}

.sortable-ghost {
  opacity: 0.4;
}

.is-dragging .drag-handle {
  opacity: 0 !important;
  pointer-events: none;
}

.sortable-chosen {
  cursor: grabbing;
}

.workflow-drag-item,
.starred-drag-item {
  position: relative;
}

.drag-handle {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 10;
  cursor: move;
  opacity: 0;
  transition: opacity 0.2s;
}

.workflow-drag-item:hover .drag-handle,
.starred-drag-item:hover .drag-handle {
  opacity: 1;
}
</style>
